import React, { useState } from "react";
import { BadgeCheck, ClipboardCheck, Download, History, UserPlus } from "lucide-react";
import Modal from "./Modal";
import { btn } from "../utils/buttonStyles";
import { useApi } from "../hooks/useApi";
import { formatUtcDateTime } from "../utils/dateTime";
import { CLOCK_LABELS, apiError, formatDateOnly, saveBlob, todayIso, userLabel } from "../utils/nercAssets";

/**
 * CIP-002 R2 on the NERC sites view: the review (R2.1) and approval (R2.2) clocks, each due 15
 * calendar months after the last one; the CIP Senior Manager and delegates (CIP-003 R3 / R4);
 * Record review (GRC managers) and Approve (only the Senior Manager, or a delegate authorised to
 * approve CIP-002, on the day — the server decides); every review / approval's frozen list as CSV;
 * and the notice where the inventory disagrees with the NERC applicability answers.
 */

function Clock({ title, clock, testId }) {
  const label = CLOCK_LABELS[clock.status] || CLOCK_LABELS.NeverRecorded;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2" data-testid={testId}>
      <div className="text-xs text-gray-500">{title}</div>
      <div className="flex items-center gap-2 mt-1">
        <span className={`px-1.5 py-0.5 rounded border text-[11px] font-semibold ${label.tone}`}>{label.text}</span>
        {clock.last && <span className="text-xs text-gray-600">Last {formatDateOnly(clock.last)} · due {formatDateOnly(clock.due)}</span>}
      </div>
    </div>
  );
}

const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm";

function HoldingForm({ kind, users, onClose, onSaved }) {
  const apiCall = useApi();
  const [form, setForm] = useState({ userId: "", effectiveFrom: todayIso(), effectiveTo: "", authorityText: "", coversCip002Approval: true, coversAuditReportApproval: false, coversPatchPlanExtension: false, reason: "" });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.userId) { setError("Choose a person."); return; }
    if (!form.reason.trim()) { setError("A reason is required."); return; }
    setSaving(true); setError(null);
    try {
      if (kind === "manager") {
        await apiCall("POST", "/nerc-assets/cip002/senior-manager", { userId: Number(form.userId), effectiveFrom: form.effectiveFrom, reason: form.reason });
      } else {
        await apiCall("POST", "/nerc-assets/cip002/delegates", {
          userId: Number(form.userId), authorityText: form.authorityText, coversCip002Approval: form.coversCip002Approval,
          coversAuditReportApproval: form.coversAuditReportApproval, coversPatchPlanExtension: form.coversPatchPlanExtension,
          effectiveFrom: form.effectiveFrom, effectiveTo: form.effectiveTo || null, reason: form.reason,
        });
      }
      onSaved();
    } catch (err) { setError(apiError(err, "Could not save.")); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title={kind === "manager" ? "Record the CIP Senior Manager" : "Add a delegate"} size="lg">
      <form onSubmit={submit} className="space-y-3" data-testid="nerc-holding-form">
        {kind === "manager" && <p className="text-sm text-gray-600">The current Senior Manager, if any, is ended the day before the new one starts. The history is kept.</p>}
        <label className="block text-xs font-medium text-gray-700">Person
          <select className={inputCls} value={form.userId} onChange={set("userId")}>
            <option value="">Choose…</option>
            {(users || []).map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-gray-700">Effective from<input type="date" className={inputCls} value={form.effectiveFrom} onChange={set("effectiveFrom")} /></label>
          {kind === "delegate" && <label className="block text-xs font-medium text-gray-700">Effective to (optional)<input type="date" className={inputCls} value={form.effectiveTo} onChange={set("effectiveTo")} /></label>}
        </div>
        {kind === "delegate" && (
          <>
            <label className="block text-xs font-medium text-gray-700">Authority delegated<textarea className={inputCls} rows={2} value={form.authorityText} onChange={set("authorityText")} maxLength={1000} /></label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.coversCip002Approval} onChange={set("coversCip002Approval")} /> May approve the CIP-002 identifications</label>
            {/* Audit Management reads this record: a NERC CIP audit's report is approved by the Senior Manager or a delegate with this. */}
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.coversAuditReportApproval} onChange={set("coversAuditReportApproval")} /> May approve NERC CIP audit reports</label>
            {/* Patch management reads this record: a mitigation plan extension (CIP-007 R2.4) needs the Senior Manager or a delegate with this. */}
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.coversPatchPlanExtension} onChange={set("coversPatchPlanExtension")} /> May approve patch mitigation plan extensions</label>
          </>
        )}
        <label className="block text-xs font-medium text-gray-700">Reason<textarea className={inputCls} rows={2} value={form.reason} onChange={set("reason")} maxLength={1000} /></label>
        {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btn("neutral", "md")}>Cancel</button>
          <button type="submit" disabled={saving} className={btn("primary", "md")}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}

function EndHoldingForm({ holding, onClose, onSaved }) {
  const apiCall = useApi();
  const [effectiveTo, setEffectiveTo] = useState(todayIso());
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setError("A reason is required."); return; }
    try { await apiCall("POST", `/nerc-assets/cip002/holdings/${holding.id}/end`, { effectiveTo, reason }); onSaved(); }
    catch (err) { setError(apiError(err, "Could not end the holding.")); }
  };
  return (
    <Modal isOpen onClose={onClose} title={`End ${holding.displayName}'s ${holding.role === "SeniorManager" ? "Senior Manager role" : "delegation"}`} size="lg">
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-xs font-medium text-gray-700">Last effective day<input type="date" className={inputCls} value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)} /></label>
        <label className="block text-xs font-medium text-gray-700">Reason<textarea className={inputCls} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} /></label>
        {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btn("neutral", "md")}>Cancel</button>
          <button type="submit" className={btn("primary", "md")}>End</button>
        </div>
      </form>
    </Modal>
  );
}

function RecordForm({ kind, onClose, onSaved }) {
  const apiCall = useApi();
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try { await apiCall("POST", `/nerc-assets/cip002/${kind === "Approval" ? "approvals" : "reviews"}`, { note }); onSaved(); }
    catch (err) { setError(apiError(err, "Could not record it.")); }
    finally { setSaving(false); }
  };
  return (
    <Modal isOpen onClose={onClose} title={kind === "Approval" ? "Approve the CIP-002 identifications" : "Record the CIP-002 review"} size="lg">
      <form onSubmit={submit} className="space-y-3" data-testid="nerc-record-form">
        <p className="text-sm text-gray-600">
          {kind === "Approval"
            ? "You approve the list of sites, BES Cyber Systems and Cyber Assets exactly as it stands now. A copy is kept that later changes never alter."
            : "Records that the identifications were reviewed today (CIP-002 R2.1). A copy of the list as reviewed is kept."}
        </p>
        <label className="block text-xs font-medium text-gray-700">Note (optional)<textarea className={inputCls} rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} /></label>
        {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btn("neutral", "md")}>Cancel</button>
          <button type="submit" disabled={saving} className={btn("primary", "md")}>{kind === "Approval" ? "Approve" : "Record review"}</button>
        </div>
      </form>
    </Modal>
  );
}

export default function NercApprovalCard({ status, canManage, users, onChanged }) {
  const apiCall = useApi();
  const [dialog, setDialog] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  if (!status) return null;

  const done = () => { setDialog(null); onChanged(); };
  const current = status.holdings.filter((h) => h.isCurrent);
  const manager = current.find((h) => h.role === "SeniorManager");
  const delegates = current.filter((h) => h.role === "Delegate");

  const download = async (record) => {
    setDownloadError(null);
    try {
      const blob = await apiCall("GET", `/nerc-assets/cip002/records/${record.id}/csv`, null, false, "blob");
      if (blob) saveBlob(blob, `nerc-cip-002-${record.kind.toLowerCase()}-${String(record.at).slice(0, 10)}.csv`);
    } catch (err) { setDownloadError(apiError(err, "Could not download the list.")); }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3" data-testid="nerc-approval-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">CIP-002 review and approval</h3>
          <p className="text-xs text-gray-500">Each is due at least every 15 calendar months.</p>
        </div>
        <div className="flex gap-2">
          {canManage && <button type="button" onClick={() => setDialog({ kind: "review" })} className={btn("secondary")}><ClipboardCheck className="h-4 w-4" />Record review</button>}
          {status.canApproveToday && <button type="button" onClick={() => setDialog({ kind: "approve" })} className={btn("primary")}><BadgeCheck className="h-4 w-4" />Approve</button>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Clock title="Review (R2.1)" clock={status.review} testId="nerc-clock-review" />
        <Clock title="CIP Senior Manager approval (R2.2)" clock={status.approval} testId="nerc-clock-approval" />
      </div>
      {status.changedSinceApproval && (
        <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2" data-testid="nerc-changed-since-approval">
          The inventory has changed since the last approval.
        </p>
      )}

      <div className="text-sm space-y-1" data-testid="nerc-senior-manager">
        <div>
          <span className="text-gray-500">CIP Senior Manager: </span>
          {manager ? <><strong>{manager.displayName}</strong> <span className="text-xs text-gray-500">since {formatDateOnly(manager.effectiveFrom)}</span></> : <span className="text-gray-500">not recorded</span>}
          {canManage && manager && <button type="button" className={`ml-2 ${btn("danger", "xs")}`} onClick={() => setDialog({ kind: "end", holding: manager })}>End</button>}
          {canManage && <button type="button" className={`ml-2 ${btn("secondary", "xs")}`} onClick={() => setDialog({ kind: "manager" })}>{manager ? "Change" : "Record"}</button>}
        </div>
        <div>
          <span className="text-gray-500">Delegates: </span>
          {delegates.length === 0 ? <span className="text-gray-500">none</span> : delegates.map((d) => (
            <span key={d.id} className="mr-3">
              <strong>{d.displayName}</strong>
              <span className="text-xs text-gray-500"> {d.coversCip002Approval ? "may approve CIP-002" : "does not approve CIP-002"}{d.coversAuditReportApproval ? " · may approve audit reports" : ""}{d.coversPatchPlanExtension ? " · may approve plan extensions" : ""}{d.effectiveTo ? ` · until ${formatDateOnly(d.effectiveTo)}` : ""}</span>
              {canManage && <button type="button" className={`ml-1 ${btn("danger", "xs")}`} onClick={() => setDialog({ kind: "end", holding: d })}>End</button>}
            </span>
          ))}
          {canManage && manager && <button type="button" className={`ml-2 ${btn("secondary", "xs")}`} onClick={() => setDialog({ kind: "delegate" })}><UserPlus className="h-3.5 w-3.5" />Add delegate</button>}
        </div>
      </div>

      {status.mismatches.length > 0 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1" data-testid="nerc-mismatches">
          <div className="font-semibold">The inventory disagrees with the NERC CIP applicability answers in the Enterprise assessment:</div>
          <ul className="list-disc ml-4">
            {status.mismatches.map((m) => <li key={m.questionId}>{m.message} <span className="text-amber-600">(answered "{m.answer}")</span></li>)}
          </ul>
          <div>Axaris does not change assessment answers. Update the answer in the assessment, or correct the inventory.</div>
        </div>
      )}

      {status.records.length > 0 && (
        <div>
          <button type="button" className={btn("neutral", "xs")} onClick={() => setShowHistory((s) => !s)}>
            <History className="h-3.5 w-3.5" />
            {showHistory ? "Hide" : "Show"} reviews and approvals ({status.recordsTotal ?? status.records.length})
          </button>
          {downloadError && <p className="text-xs text-red-700" role="alert">{downloadError}</p>}
          {showHistory && status.recordsTotal > status.records.length && (
            <p className="text-xs text-gray-500 mt-1">Showing the latest {status.records.length} of {status.recordsTotal}.</p>
          )}
          {showHistory && (
            <table className="w-full text-xs mt-2 bg-white border border-gray-200 rounded-lg" data-testid="nerc-records">
              <thead><tr className="text-left text-gray-500"><th className="px-2 py-1">When</th><th>Kind</th><th>By</th><th>List</th><th>Note</th><th /></tr></thead>
              <tbody>
                {status.records.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-2 py-1">{formatUtcDateTime(r.at)}</td>
                    <td>{r.kind === "Approval" ? "Approval" : "Review"}</td>
                    <td>{r.displayName}{r.capacity === "Delegate" ? " (delegate)" : r.capacity === "SeniorManager" ? " (Senior Manager)" : ""}</td>
                    <td>{r.sites} sites · {r.highSystems}H / {r.mediumSystems}M / {r.lowSystems}L systems · {r.cyberAssets} Cyber Assets</td>
                    <td>{r.note || "—"}</td>
                    <td className="px-2"><button type="button" className={btn("secondary", "xs")} onClick={() => download(r)}><Download className="h-3.5 w-3.5" />Download list</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {(dialog?.kind === "manager" || dialog?.kind === "delegate") && <HoldingForm kind={dialog.kind} users={users} onClose={() => setDialog(null)} onSaved={done} />}
      {dialog?.kind === "end" && <EndHoldingForm holding={dialog.holding} onClose={() => setDialog(null)} onSaved={done} />}
      {dialog?.kind === "review" && <RecordForm kind="Review" onClose={() => setDialog(null)} onSaved={done} />}
      {dialog?.kind === "approve" && <RecordForm kind="Approval" onClose={() => setDialog(null)} onSaved={done} />}
    </div>
  );
}
